import { useEffect, useRef } from "react"
import { cn } from "@/lib/utils"
import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"

gsap.registerPlugin(ScrollTrigger)

const cards = [
  {
    label: "Problem",
    title: "Talent Is Scarce, Cost Is Rising",
    description:
      "Fullchip experts are hard to hire, design costs keep climbing, and time-to-market keeps shrinking.",
    featured: false,
  },
  {
    label: "Solution",
    title: "A Dedicated ODC Model",
    description:
      "Aexiom runs as your offshore design centre — experienced engineers, embedded in your flow, focused entirely on your tape-out.",
    featured: true,
  },
  {
    label: "Opportunity",
    title: "Demand Is Accelerating",
    description:
      "AI, automotive, data centre and edge applications are driving outsourced physical design demand higher than ever.",
    featured: false,
  },
]

export function GapSection() {
  const sectionRef = useRef<HTMLElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const gridRef = useRef<HTMLDivElement>(null)
  const accentLineRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!sectionRef.current || !headerRef.current || !gridRef.current) return

    const ctx = gsap.context(() => {
      gsap.fromTo(
        headerRef.current,
        { x: -60, opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: headerRef.current,
            start: "top 85%",
            toggleActions: "play none none reverse",
          },
        },
      )

      const articles = gridRef.current?.querySelectorAll("article")
      if (articles?.length) {
        gsap.fromTo(
          articles,
          { x: -80, opacity: 0 },
          {
            x: 0,
            opacity: 1,
            duration: 0.85,
            stagger: 0.12,
            ease: "power3.out",
            scrollTrigger: {
              trigger: gridRef.current,
              start: "top 88%",
              toggleActions: "play none none reverse",
            },
          },
        )
      }

      if (accentLineRef.current) {
        gsap.fromTo(
          accentLineRef.current,
          { scaleX: 0 },
          {
            scaleX: 1,
            duration: 0.9,
            ease: "power3.inOut",
            scrollTrigger: {
              trigger: accentLineRef.current,
              start: "top 90%",
              toggleActions: "play none none reverse",
            },
          },
        )
      }
    }, sectionRef)

    return () => ctx.revert()
  }, [])

  return (
    <section ref={sectionRef} id="gap" className="relative border-t border-border/30 py-32 pl-6 md:pl-28">
      <div ref={headerRef} className="mb-16 max-w-3xl pr-6 md:pr-12">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-accent">The Gap</span>
        <h2 className="mt-4 font-[var(--font-bebas)] text-5xl leading-[1.05] tracking-tight uppercase md:text-7xl">
          The Fullchip Expertise Is Scarce,
          <br />
          The Deadlines Aren&apos;t.
        </h2>
      </div>

      <div ref={gridRef} className="grid max-w-6xl gap-4 pr-6 md:grid-cols-3 md:gap-6 md:pr-12">
        {cards.map((card) => (
          <article
            key={card.label}
            className={cn(
              "relative flex flex-col border border-border/30 bg-background/40 p-6 text-left transition-colors duration-300 hover:bg-foreground/[0.02] md:p-8",
              card.featured && "border-t-0",
            )}
          >
            {card.featured && (
              <div className="absolute inset-x-0 top-0 h-0.5 overflow-hidden bg-border/20">
                <div ref={accentLineRef} className="h-full w-full origin-left scale-x-0 bg-accent" />
              </div>
            )}

            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-accent">{card.label}</span>
            <h3
              className={cn(
                "mt-4 font-[var(--font-bebas)] text-2xl leading-tight tracking-tight uppercase md:text-3xl",
                card.featured && "whitespace-nowrap",
              )}
            >
              {card.title}
            </h3>
            <p className="mt-4 font-mono text-xs leading-relaxed text-muted-foreground md:text-sm">
              {card.description}
            </p>
          </article>
        ))}
      </div>
    </section>
  )
}
